import { ArrowRight } from '@phosphor-icons/react';

/**
 * Reusable list-table component matching the Ecowater Cloud aesthetic.
 *
 * columns: Array<{
 *   key: string,
 *   label: string,
 *   width: string,                   // CSS grid-column value (e.g. 'minmax(160px,1.2fr)')
 *   align?: 'left' | 'right',
 *   mono?: boolean,                  // monospace text
 *   tone?: 'strong' | 'default' | 'muted',
 *   render?: (row) => ReactNode      // custom cell renderer; falls back to row[key]
 * }>
 *
 * Each row is a clickable pill-card. On hover, the entire row turns brand-blue.
 * Always renders an "Action" column with a circular arrow button on the right.
 */
export function DataTable({
  columns,
  rows,
  loading = false,
  rowKey,
  onRowClick,
  emptyIcon = null,
  emptyTitle = 'No records',
  emptyText = 'Records will appear here.',
  testIdPrefix = 'table',
  actionRender,
}) {
  const ACTION_W = 56;
  const gridCols = [...columns.map((c) => c.width || '1fr'), `${ACTION_W}px`].join(' ');

  if (loading) {
    // Structured table-row skeleton — mirrors the actual column layout so
    // the loaded data slides into place without layout shift. Uses the same
    // grid-template-columns inferred from the `columns` prop.
    const gridCols = (columns || []).map(c => c.width || '1fr').join(' ') + ' auto';
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-loading`}>
        {Array.from({ length: 8 }).map((_, r) => (
          <div
            key={r}
            className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl px-5 py-3.5 grid items-center gap-3"
            style={{ gridTemplateColumns: gridCols }}
          >
            {(columns || []).map((_c, c) => (
              <div
                key={c}
                className="skeleton h-3.5"
                style={{ width: `${55 + ((r + c) % 5) * 9}%` }}
              />
            ))}
            <div className="skeleton h-7 w-7 justify-self-end" />
          </div>
        ))}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-3xl py-20 text-center" data-testid={`${testIdPrefix}-empty`}>
        <div className="w-14 h-14 rounded-2xl bg-[#F2EBDC] flex items-center justify-center mx-auto mb-3">
          {emptyIcon}
        </div>
        <p className="text-sm font-semibold text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{emptyTitle}</p>
        <p className="text-xs text-[#78716C] mt-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{emptyText}</p>
      </div>
    );
  }

  const toneClass = (tone) => {
    if (tone === 'muted') return 'text-[#78716C] group-hover:text-[#1C1917]';
    if (tone === 'strong') return 'font-semibold text-[#1C1917] group-hover:text-[#1C1917]';
    return 'text-[#1C1917] group-hover:text-[#1C1917]';
  };

  // Combined minimum column width — used to enable horizontal scroll on
  // small screens so each cell renders at full width instead of being
  // crushed. Pulled from the `minmax(NNNpx, …)` portion of each column's
  // width definition; defaults to 120px when not parseable.
  const minTableWidth = (() => {
    const parse = (w) => {
      if (!w) return 120;
      const m = /minmax\(\s*(\d+)\s*px/.exec(w);
      if (m) return parseInt(m[1], 10);
      const m2 = /^(\d+)\s*px$/.exec(w);
      return m2 ? parseInt(m2[1], 10) : 120;
    };
    return columns.reduce((sum, c) => sum + parse(c.width), 0) + ACTION_W + (columns.length + 1) * 16;
  })();

  return (
    <div
      className="overflow-x-auto -mx-1 px-1 lg:overflow-visible lg:mx-0 lg:px-0"
      data-testid={`${testIdPrefix}-scroll`}
    >
      <div className="space-y-2 lg:min-w-0" style={{ minWidth: minTableWidth }} data-testid={`${testIdPrefix}-table`}>
      {/* Column header */}
      <div
        className="grid items-center gap-4 px-5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A8A29E]"
        style={{ gridTemplateColumns: gridCols, fontFamily: 'JetBrains Mono, monospace' }}
      >
        {columns.map((c) => (
          <div key={c.key} className={c.align === 'right' ? 'text-right' : ''}>{c.label}</div>
        ))}
        <div className="text-right">Action</div>
      </div>

      {/* Rows */}
      {rows.map((row) => {
        const key = String(rowKey(row));
        return (
          <div
            key={key}
            data-testid={`${testIdPrefix}-row-${key}`}
            onClick={() => onRowClick && onRowClick(row)}
            className={`group grid items-center gap-4 bg-[#FFFFFF] border border-[#EDE6D8] rounded-2xl px-5 py-3
              shadow-[0_1px_2px_rgba(28,25,23,0.04)]
              hover:border-[#1C1917]/15 hover:shadow-[0_8px_24px_-12px_rgba(28,25,23,0.15)]
              transition-all duration-200 ${onRowClick ? 'cursor-pointer' : ''}`}
            style={{ gridTemplateColumns: gridCols }}
          >
            {columns.map((c) => {
              const content = c.render ? c.render(row) : row[c.key];
              return (
                <div
                  key={c.key}
                  className={[
                    'text-sm',
                    c.wrap ? 'whitespace-normal break-words leading-snug' : 'truncate',
                    c.mono ? 'font-mono' : '',
                    c.align === 'right' ? 'text-right' : '',
                    toneClass(c.tone),
                  ].filter(Boolean).join(' ')}
                  style={!c.mono ? { fontFamily: 'Plus Jakarta Sans, sans-serif' } : undefined}
                >
                  {content == null || content === '' ? '—' : content}
                </div>
              );
            })}
            <div className="flex items-center justify-end">
              {actionRender ? actionRender(row) : (
                <button
                  onClick={(e) => { e.stopPropagation(); onRowClick && onRowClick(row); }}
                  data-testid={`${testIdPrefix}-action-${key}`}
                  className="w-8 h-8 rounded-full flex items-center justify-center
                    bg-[#FCFAF6] text-[#0A2540]
                    group-hover:bg-[#0A2540] group-hover:text-[#1C1917]
                    transition-colors"
                  aria-label="View"
                >
                  <ArrowRight size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
